import { Box, Link, system, SystemProps, Text } from "flicket-ui";
import Image from "next/future/image";
import { ReactNode, useContext, useState } from "react";
import styled from "styled-components";
import { Icon } from "~components/common/Icon";
import { Sidebar } from "~components/common/Menu/menu.Sidebar";
import { BaseContext } from "~context";
import { getImage } from "~lib/helpers";

import { ContainedImage } from "../Image";
import { RoundedButton } from "./menu.RoundedButton";

interface MenuProps extends SystemProps {
  isLoggedIn: boolean;
  pathname: string;
  logout: () => void;
  children: ReactNode;
}

const StyledMenu = styled(Box)<SystemProps>`
  width: 100%;
  padding: 16px 16px 16px 16px;

  height: 128px;
  max-height: 128px;

  /* background-color: ${(p) => p.theme.colors.P300}; */

  box-shadow: ${(p) => p.theme.shadows.sm};

  font-size: ${(p) => p.theme.fontSizes[3]};
  letter-spacing: -0.165px;

  position: relative;

  ${system}
`;

const MobileBackground = styled(Box)`
  position: absolute;

  max-height: 128px;
  height: 128px;

  top: 0;
  left: 0;

  width: 100%;

  background-color: ${(p) => p.theme.colors.P300};

  z-index: 1;
`;

const visiblePaths = [
  "/",
  "/events",
  "/merchandise",
  "/memberships",
  "/members-area",
  "/not-found",
  "/404",
  "/account",
  "/tickets",
  "/order",
  "/tours/[tourId]",
  "/calendar",
];

export const MobileMenu = ({
  isLoggedIn,
  logout,
  pathname = "/",
  children,
  ...props
}: MenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { organization, isSinglePage } = useContext(BaseContext);
  const { mobileLogo, background } = organization?.branding || {};

  // this is needed so we have the menu header for a single event set up.
  if (isSinglePage) {
    visiblePaths.push("/events/[eventId]");
  }

  const openMenu = () => setIsOpen(true);
  const closeMenu = () => setIsOpen(false);
  const bgImageUrl = getImage(organization?.branding?.background, "");
  return (
    visiblePaths.includes(pathname) && (
      <StyledMenu d="flex" flexDirection="column" {...props}>
        <MobileBackground>
          {bgImageUrl && (
            <Image
              src={bgImageUrl}
              fill={true}
              alt={`${organization.name}`}
              priority={true}
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
          )}
        </MobileBackground>

        <Sidebar
          isLoggedIn={isLoggedIn}
          isOpen={isOpen}
          logout={logout}
          onClose={closeMenu}
        />
        <Box
          d="flex"
          alignItems="center"
          justifyContent="space-between"
          zIndex={"2" as any}
        >
          <Box d="flex">
            <Icon
              fontSize={6}
              icon="menu"
              color="white"
              mr="6/4"
              onClick={openMenu}
            />
            <Link
              to={
                organization?.slug === "artfair"
                  ? "http://www.artfair.co.nz"
                  : "/"
              }
            >
              <ContainedImage
                image={mobileLogo}
                fallback="/static/flicket-white.png"
                alt={`${organization.name} logo`}
                sizes={"(min-width: 200px) 200px"}
                priority={true}
                width={60}
                height={40}
              />
            </Link>
          </Box>
          {!isSinglePage && !!organization?.showMemberships && (
            <Box d="flex" alignItems="center">
              <RoundedButton
                ml={4}
                primary={!!background}
                to={
                  isLoggedIn
                    ? "/members-area"
                    : "/login?redirect=%2Fmembers-area"
                }
              >
                <Icon
                  fontSize={5}
                  mr="3/4"
                  icon="membership"
                  color={background ? "white" : undefined}
                />
                <Text
                  variant="heavy.L"
                  lineHeight="medium"
                  color={background ? "white" : undefined}
                >
                  Members Area
                </Text>
              </RoundedButton>
            </Box>
          )}
        </Box>
        {children}
      </StyledMenu>
    )
  );
};
